import React, { useState, useEffect, useRef } from 'react';
import {
  MessageCircle, ArrowRight, ChevronDown, Check,
  Leaf, BarChart3, Heart, Scale, Camera, FileText,
  Zap, BookOpen, Lamp, Speaker, Smartphone,
  PenTool, BookMarked
} from 'lucide-react';

const WA_LINK = "https://wa.me/601171383699";
const DRAGON_IMG = "https://static.prod-images.emergentagent.com/jobs/bb69c14e-e721-4f53-aed8-ca0b062ed855/images/e4efc59aa0adc9db4e46a233084fc5a49b8364492eb98cbd2cb077cc868c32a2.png";
const TIGER_IMG = "https://static.prod-images.emergentagent.com/jobs/bb69c14e-e721-4f53-aed8-ca0b062ed855/images/0a893322de0acef6cc74feaab03898bcd3beceda7423e0ccf45f1b9dc20c49c5.png";
const DESK_IMG = "https://images.pexels.com/photos/273238/pexels-photo-273238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CTAButton = ({ text = "BOOK YOUR AUDIT NOW", className = "" }) => (
  <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
    className={`inline-flex items-center gap-3 bg-[#2D5016] hover:bg-[#3A6B1E] text-white font-bold uppercase tracking-widest rounded-lg transition-all duration-300 hover:shadow-[0_10px_40px_-10px_rgba(45,80,22,0.6)] hover:scale-[1.02] active:scale-[0.98] px-10 py-5 text-base ${className}`}>
    <MessageCircle className="w-5 h-5" />
    <span>{text}</span>
    <ArrowRight className="w-5 h-5" />
  </a>
);

const AnimatedSection = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={ref} id={id} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </section>
  );
};

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#2D5016]/30">
      <button className="w-full flex items-center justify-between py-5 text-left group" onClick={() => setOpen(!open)}>
        <span className="text-[#1a1a1a] font-medium pr-4 group-hover:text-[#2D5016] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#2D5016] shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-40 pb-5' : 'max-h-0'}`}>
        <p className="text-[#5a5a4a] leading-relaxed">{a}</p>
      </div>
    </div>
  );
};

const FengShuiDeskPage = () => {
  return (
    <div className="min-h-screen bg-[#E8E0D0] text-[#1a1a1a] overflow-x-hidden pt-16">

      {/* ===== TOP BANNER IMAGE ===== */}
      <div className="relative w-full overflow-hidden">
        {/* Desktop banner */}
        <img
          src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/30tk2gs6_Screenshot%202026-04-25%20at%208.20.39%20PM.png"
          alt="Personal Desktop Feng Shui Audit"
          className="w-full h-auto hidden sm:block"
        />
        {/* Mobile banner */}
        <img
          src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/kg7gf83u_Screenshot%202026-04-25%20at%208.27.19%20PM.png"
          alt="Personal Desktop Feng Shui Audit"
          className="w-full h-auto block sm:hidden"
        />
      </div>

      {/* ===== WHAT YOU RECEIVE + PRICING ===== */}
      <AnimatedSection className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12 text-center">
            What You'll Receive in Your{' '}<span className="text-[#2D5016]">Personal Desktop Feng Shui Audit</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* Checklist */}
            <div className="space-y-4">
              {[
                "Personalized analysis of your desktop energy layout",
                "Identify energy imbalances and hidden blockers",
                "Customized recommendations for layout, placement & enhancements",
                "Actionable tips to align your desk with your goals",
                "A clearer, more powerful space that supports your success",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-[#2D5016] flex items-center justify-center mt-0.5">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-[#1a1a1a] text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>

            {/* Your Desk, Your Energy */}
            <div className="p-8 bg-[#2D5016]/5 border-2 border-[#2D5016]/15 rounded-2xl text-center">
              <h3 className="text-2xl font-bold text-[#1a1a1a] leading-tight">Your Desk.<br />Your Energy.<br /><span className="text-[#2D5016]">Your Success.</span></h3>
              <p className="text-[#5a5a4a] text-sm leading-relaxed mt-4">
                Small changes in your space create big shifts in your life. Let's align your desk with prosperity, clarity, and balance.
              </p>
            </div>

            {/* Price Card */}
            <div className="p-6 bg-[#f5f0e6] border-2 border-[#2D5016]/25 rounded-2xl text-center shadow-lg" data-testid="pricing-card">
              <p className="text-xs text-[#8B8B7A] uppercase tracking-widest mb-2">Your Investment</p>
              <div className="mb-2">
                <span className="text-lg text-[#8B8B7A]">USD</span>{' '}
                <span className="text-5xl font-black text-[#1a1a1a]">2,288</span>
              </div>
              <p className="text-xs text-[#5a5a4a] mb-4">A small investment for a lifetime of clarity, opportunities, and success.</p>
              <CTAButton className="w-full justify-center text-sm px-6 py-4" />
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== HOW IT WORKS ===== */}
      <AnimatedSection className="py-16 px-6 bg-[#2D5016]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold mb-10 text-center text-white uppercase tracking-[0.2em]">
            Book Your Personal Desktop Feng Shui Audit Today
          </h2>
          <div className="flex flex-col items-center gap-0 max-w-md mx-auto">
            {[
              { step: "1", icon: BookOpen, title: "Book Your Session" },
              { step: "2", icon: Camera, title: "Send a Photo of Your Desk" },
              { step: "3", icon: FileText, title: "Receive Your Feng Shui Report" },
              { step: "4", icon: Zap, title: "Take Action & Transform Your Results" },
            ].map((item, i, arr) => {
              const Icon = item.icon;
              return (
                <React.Fragment key={i}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-2">
                      <Icon className="w-6 h-6 text-[#C5D5A0]" />
                    </div>
                    <p className="text-[#C5D5A0] text-xs font-bold">Step {item.step}</p>
                    <h3 className="font-semibold text-white text-base">{item.title}</h3>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="py-1">
                      <div className="w-[2px] h-6 bg-[#C5D5A0]/30 mx-auto relative">
                        <ChevronDown className="w-4 h-4 text-[#C5D5A0] absolute -bottom-2 left-1/2 -translate-x-1/2" />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FAQ ===== */}
      <AnimatedSection id="faq" className="py-20 px-6 bg-[#f5f0e6]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-10 text-center">
            Common <span className="text-[#2D5016]">Questions</span>
          </h2>
          <div>
            <FaqItem q="What is the Green Dragon and White Tiger formation?" a="In Feng Shui, the Green Dragon (left side) represents movement, growth, and expansion, while the White Tiger (right side) represents stability, control, and protection. Balancing these two forces on your desk creates harmonious energy flow." />
            <FaqItem q="Do I need to be in person for the audit?" a="No. Simply send a clear photo of your desk via WhatsApp and Master Steven will conduct a thorough analysis remotely. You'll receive a personalized report with specific recommendations." />
            <FaqItem q="How quickly will I see results?" a="Many clients notice shifts in productivity, clarity, and energy within the first week of implementing changes. The key is following the specific placement recommendations in your audit report." />
            <FaqItem q="What if I have a small desk or limited space?" a="Feng Shui works with any space. Master Steven will provide recommendations tailored to your specific desk size and constraints. Even small adjustments can create significant energy shifts." />
            <FaqItem q="Is this only for office desks?" a="This audit works for any workspace — home office, corporate desk, study table, or creative studio. Anywhere you sit and work can benefit from proper Dragon-Tiger alignment." />
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 px-6 bg-[#E8E0D0] relative" data-testid="final-cta">
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-[#1a1a1a]">
            Your Space. Your Energy. Your Life.
          </h2>
          <p className="text-[#2D5016] text-lg font-bold mb-8">
            Let's Make It Extraordinary.
          </p>
          <CTAButton text="BOOK YOUR AUDIT ON WHATSAPP" />
          <p className="text-xs text-[#8B8B7A] mt-6">USD 2,288 · Personal Desktop Feng Shui Audit · Master Steven Yong</p>
        </div>
      </section>
    </div>
  );
};

export default FengShuiDeskPage;
